import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import { enableAutoSuggest, bucketUrlPrefix } from "common/config";
import {
  getUnifiedSuggestions,
  type UnifiedSuggestItem,
  RecentSearchResult,
} from "common/apis";
import { useFilters, useI18N, useBrands } from "common/hooks";
import { trackEvent } from "common/analytics";
import {
  SearchSuggestionsDropdown,
  type NeighborhoodSuggestion,
} from "@/components/Search/FilterPannel/SearchSuggestions";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  TrainIcon,
  AirplaneTakeoffIcon,
} from "@phosphor-icons/react";
import {
  CookieIcon,
  CoffeeIcon,
  MartiniIcon,
  ForkKnifeIcon,
  ShoppingCartIcon,
} from "@phosphor-icons/react";
import {
  PlainRingPin,
  VerifiedSociabilityPin,
  PIN_ASPECT,
  getPinCentreTop,
  ORANGE,
  RING_STROKE,
} from "@/components/Map/AdvancedPinContent";
import { BusinessTypeGlyph } from "@/components/Map/BusinessTypeGlyph";

// Re-export types for external use
export type { RecentSearchResult, NeighborhoodSuggestion, UnifiedSuggestItem };

type AutoSuggestInputProps = {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (text: string) => void;
  /** Fired on tap: consumer dispatches item.action. sessionToken closes the Google session on the follow-up call. */
  onSelectUnified: (item: UnifiedSuggestItem, sessionToken: string) => void;
  onClear?: () => void;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  className?: string;
  tagging_mode?: string;
  types?: string;
  accessibilities?: string;
  showVenueOptions?: string;
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick?: () => void;
  onRecentSearchSelect?: (search: RecentSearchResult) => void;
  onNeighborhoodSelect?: (neighborhood: NeighborhoodSuggestion) => void;
  onLoginPress?: () => void;
  /** Whether to show the SearchSuggestionsDropdown when input is empty/focused */
  showSearchSuggestions?: boolean;
  /** Suppress the autosuggest dropdown (e.g., when saved searches is open) */
  suppressDropdown?: boolean;
  /** Layout mode - determines positioning and wrapper behavior */
  layout?: "default" | "header" | "overlay" | "overlaymobile";
};

// Remove HTML tags and normalise whitespace.
const sanitizeInput = (text: string): string => {
  const withoutHtml = text.replace(/<[^>]*>/g, "");
  return withoutHtml.trim().replace(/\s+/g, " ");
};

// Generate a session token (used to batch autocomplete keystrokes for billing).
const generateSessionToken = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual generation
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Circular icon badge: purple for locations/stations/categories, grey for search.
const IconBadge = ({
  tone,
  children,
}: {
  tone: "purple" | "grey";
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
      tone === "purple" ? "bg-bg-brand-tertiary-active" : "bg-pin-grey",
    )}
  >
    {children}
  </div>
);

// Same pin used on the map (orange+verified for mapped venues, grey outline
// for unmapped), with the business-type glyph centred inside.
const VENUE_PIN_SIZE = 26;
const VenuePinIcon = ({
  pinType,
  businessType,
}: {
  pinType: "orange" | "grey";
  businessType: string | null;
}) => {
  const color = pinType === "grey" ? RING_STROKE : ORANGE;
  const glyphSize = VENUE_PIN_SIZE * 0.4;
  const centreTop = getPinCentreTop(VENUE_PIN_SIZE);

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: VENUE_PIN_SIZE, height: VENUE_PIN_SIZE * PIN_ASPECT }}
    >
      {pinType === "grey" ? (
        <PlainRingPin size={VENUE_PIN_SIZE} color={color} />
      ) : (
        <VerifiedSociabilityPin size={VENUE_PIN_SIZE} />
      )}
      <span
        className="absolute pointer-events-none"
        style={{ left: "50%", top: centreTop, transform: "translate(-50%, -50%)" }}
      >
        <BusinessTypeGlyph businessType={businessType} size={glyphSize} color={color} />
      </span>
    </div>
  );
};

// Component to display business type icons
const BusinessTypeIcon = ({ businessType }: { businessType: string }) => {
  const { businessTypesFrontend } = useFilters();
  const [iconPath, setIconPath] = useState<string | null>(null);

  // First check for specific hardcoded business type icons
  const hasSpecificIcon = [
    "bakery",
    "cafe",
    "bar",
    "restaurant",
    "supermarket",
    "food",
    "store",
  ].includes(businessType);

  // Find the matching business type and get its icon from businessTypesFrontend
  useEffect(() => {
    // Skip fetching if we have a specific icon
    if (hasSpecificIcon) return;

    if (businessTypesFrontend && businessType) {
      const matchingFrontendType = businessTypesFrontend.find(
        (type) => type.name.toLowerCase() === businessType.toLowerCase(),
      );
      if (matchingFrontendType && matchingFrontendType.image) {
        setIconPath(bucketUrlPrefix + matchingFrontendType.image);
        return;
      }
    }
  }, [businessTypesFrontend, businessType, hasSpecificIcon]);

  // Render specific icon if available
  if (hasSpecificIcon) {
    const icons: Record<string, ReactNode> = {
      bakery: <CookieIcon size={18} className="text-fg-brand-subtle" />,
      cafe: <CoffeeIcon size={18} className="text-fg-brand-subtle" />,
      bar: <MartiniIcon size={18} className="text-fg-brand-subtle" />,
      restaurant: <ForkKnifeIcon size={18} className="text-fg-brand-subtle" />,
      food: <ShoppingCartIcon size={18} className="text-fg-brand-subtle" />,
      supermarket: <ShoppingCartIcon size={18} className="text-fg-brand-subtle" />,
      store: <ShoppingCartIcon size={18} className="text-fg-brand-subtle" />,
    };
    if (icons[businessType]) {
      return <IconBadge tone="purple">{icons[businessType]}</IconBadge>;
    }
  }

  // Render dynamic icon from businessTypesFrontend if found
  if (iconPath) {
    return (
      <IconBadge tone="purple">
        <img
          src={iconPath}
          alt={businessType}
          className="w-[18px] h-[18px] object-contain filter brightness-0"
        />
      </IconBadge>
    );
  }

  // Fallback icon if no matching business type is found
  return (
    <IconBadge tone="purple">
      <MapPinIcon size={18} className="text-fg-brand-subtle" />
    </IconBadge>
  );
};

// Component to display brand logo or first character
interface BrandLogoProps {
  brandName: string;
  latitude: number;
  longitude: number;
}

const BrandLogo = ({ brandName, latitude, longitude }: BrandLogoProps) => {
  const { data: brandsData } = useBrands({
    latitude,
    longitude,
    enabled:
      !!brandName &&
      !!latitude &&
      !!longitude &&
      !isNaN(latitude) &&
      !isNaN(longitude),
  });

  const matchingBrand = brandsData?.facets?.brand?.find(
    (b) => b.value.toLowerCase() === brandName.toLowerCase(),
  );

  const imageUrl = matchingBrand?.logo
    ? `${bucketUrlPrefix}${matchingBrand.logo}`
    : null;

  return (
    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-container-neutral-minimal overflow-hidden flex-shrink-0">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={brandName}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="label-s-semibold text-fg-neutral-strong">
          {brandName.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
};

export default function AutoSuggestInput({
  label,
  value,
  onValueChange,
  onSubmit,
  onSelectUnified,
  onClear,
  latitude = 0,
  longitude = 0,
  radiusKm = 10,
  className,
  tagging_mode,
  types,
  accessibilities,
  showVenueOptions,
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick,
  onRecentSearchSelect,
  onNeighborhoodSelect,
  onLoginPress,
  showSearchSuggestions = false,
  suppressDropdown = false,
  layout = "default" as "default" | "header" | "overlay" | "overlaymobile",
}: AutoSuggestInputProps) {
  const i18n = useI18N();
  const [items, setItems] = useState<UnifiedSuggestItem[]>([]);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [isSearchSuggestionsOpen, setIsSearchSuggestionsOpen] = useState(false);

  // Session token: generated on first keystroke, reused across keystrokes, reset
  // on selection / clear (a fresh search starts a new session).
  const sessionTokenRef = useRef<string>("");
  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }
    return sessionTokenRef.current;
  }, []);
  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = "";
  }, []);

  // search_list_viewed / search_list_closed.
  //  - Inline bars (the desktop-home "header" layout, always mounted): the empty-input
  //    suggestions panel opens on focus and closes on blur — fire on the
  //    isSearchSuggestionsOpen transition (below).
  //  - Overlay bars ("overlay" = the global Navbar search on every non-home page;
  //    "overlaymobile" = the mobile search on home AND the results page): the whole
  //    overlay IS the search list and MOUNTS on open / UNMOUNTS on close. Fire viewed
  //    on mount / closed on unmount so both fire outside the homepage too (and
  //    regardless of any pre-filled text), and gate the inline transition/focus fires
  //    OFF for overlays so we don't double-count.
  const isOverlayLayout = layout === "overlay" || layout === "overlaymobile";
  useEffect(() => {
    if (!isOverlayLayout) return;
    trackEvent("search_list_viewed", {});
    return () => {
      trackEvent("search_list_closed", {});
    };
  }, [isOverlayLayout]);

  // search_list_closed: fire once per open->closed transition of the empty-input search-list overlay
  const searchListOpenRef = useRef(false);
  useEffect(() => {
    if (searchListOpenRef.current && !isSearchSuggestionsOpen && !isOverlayLayout) {
      trackEvent("search_list_closed", {});
    }
    searchListOpenRef.current = isSearchSuggestionsOpen;
  }, [isSearchSuggestionsOpen, isOverlayLayout]);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const suggestDebounceRef = useRef<number | null>(null);
  const suggestListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when component mounts
  useEffect(() => {
    if (layout === "overlay" || layout === "overlaymobile") {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [layout]);

  // Close dropdowns when suppressDropdown becomes true
  useEffect(() => {
    if (suppressDropdown) {
      setIsSuggestOpen(false);
      setIsSearchSuggestionsOpen(false);
    }
  }, [suppressDropdown]);

  useEffect(() => {
    if (!isSuggestOpen) return;
    if (highlightIndex < 0) return;
    const activeEl = document.getElementById(`suggest-item-${highlightIndex}`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, isSuggestOpen]);

  // Backend owns ordering; FE only appends the "Press Enter" query row as a
  // fallback, since free text is less reliable than a real suggestion match.
  const displayItems = useMemo(() => {
    const out: Array<
      | { kind: "query"; text: string }
      | { kind: "item"; data: UnifiedSuggestItem }
    > = [];
    for (const it of items) {
      out.push({ kind: "item", data: it });
    }
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      out.push({ kind: "query", text: trimmed });
    }
    return out;
  }, [value, items]);

  const clearSuggestions = useCallback(() => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setItems([]);
    setIsSuggestOpen(false);
    setIsSearchSuggestionsOpen(false);
    setHighlightIndex(-1);
  }, []);

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (isSuggestOpen || isSearchSuggestionsOpen) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        clearSuggestions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuggestOpen, isSearchSuggestionsOpen, clearSuggestions]);

  // Render displayText with the backend-computed highlight ranges in bold.
  const renderHighlighted = (text: string, ranges: [number, number][]) => {
    if (!text) return <>{text}</>;
    if (!ranges || ranges.length === 0) return <>{text}</>;
    const sorted = [...ranges]
      .filter((r) => Array.isArray(r) && r.length === 2)
      .sort((a, b) => a[0] - b[0]);
    if (sorted.length === 0) return <>{text}</>;
    const parts: ReactNode[] = [];
    let cursor = 0;
    sorted.forEach(([start, end], i) => {
      const s = Math.max(start, cursor);
      if (s > end) return;
      if (s > cursor) {
        parts.push(<span key={`n${i}`}>{text.slice(cursor, s)}</span>);
      }
      parts.push(<b key={`b${i}`}>{text.slice(s, end)}</b>);
      cursor = Math.max(cursor, end);
    });
    if (cursor < text.length) {
      parts.push(<span key="rest">{text.slice(cursor)}</span>);
    }
    return <>{parts}</>;
  };

  // iconKey → icon; in-DB venues use the orange/grey pin from data.pinType.
  const renderItemIcon = (item: UnifiedSuggestItem) => {
    const { iconKey, data } = item;

    if (data.pinType === "orange" || data.pinType === "grey") {
      return <VenuePinIcon pinType={data.pinType} businessType={data.businessType} />;
    }

    switch (iconKey) {
      case "venue":
        // Not (yet) in our DB — same grey pin as an unmapped venue, no
        // separate "generic place" icon.
        return <VenuePinIcon pinType="grey" businessType={data.businessType} />;
      case "brand":
        return (
          <BrandLogo
            brandName={data.brand || ""}
            latitude={latitude || 0}
            longitude={longitude || 0}
          />
        );
      case "area":
        return (
          <IconBadge tone="purple">
            <MapPinIcon size={18} weight="fill" className="text-fg-brand-subtle" />
          </IconBadge>
        );
      case "station":
        return (
          <IconBadge tone="purple">
            <TrainIcon size={18} className="text-fg-brand-subtle" />
          </IconBadge>
        );
      case "airport":
        return (
          <IconBadge tone="purple">
            <AirplaneTakeoffIcon size={18} className="text-fg-brand-subtle" />
          </IconBadge>
        );
      case "search":
        return (
          <IconBadge tone="grey">
            <MagnifyingGlassIcon size={18} className="text-fg-neutral-medium" />
          </IconBadge>
        );
      case "location":
        return (
          <IconBadge tone="purple">
            <MapPinIcon size={18} weight="fill" className="text-fg-brand-subtle" />
          </IconBadge>
        );
      default:
        // Any other iconKey is a business-type slug ("cafe", "bar", ...).
        return <BusinessTypeIcon businessType={iconKey} />;
    }
  };

  // Localized CTA suffix appended to the factual backend subtitle, keyed off action type.
  const ctaSuffix = (item: UnifiedSuggestItem): string => {
    switch (item.action.type) {
      case "PAN_AND_NEARBY":
        return item.iconKey === "airport"
          ? i18n.get("searchNearAirport")
          : item.iconKey === "station"
            ? i18n.get("searchNearStation")
            : i18n.get("searchThisArea");
      case "CATEGORY_IN_AREA":
        return i18n.get("searchInThisArea");
      case "BRAND":
        return i18n.get("seeLocations");
      default:
        return "";
    }
  };

  const subtitleLine = (item: UnifiedSuggestItem): string => {
    const cta = ctaSuffix(item);
    return [item.subtitle, cta].filter(Boolean).join(" \u2022 ");
  };

  // Synthetic display-only labels (e.g. results-header text) that sometimes
  // end up echoed into this editable box. Never treat them as a real query.
  const isSyntheticLabel = useCallback(
    (text: string) => {
      const normalized = text.trim().toLowerCase();
      return [i18n.get("nearBy"), i18n.get("currentLocation")].some(
        (label) => label && label.trim().toLowerCase() === normalized,
      );
    },
    [i18n],
  );

  // Fetch suggestions from the unified backend endpoint (single call).
  const fetchSuggestions = useCallback(
    async (text: string): Promise<UnifiedSuggestItem[]> => {
      if (isSyntheticLabel(text)) return [];
      return getUnifiedSuggestions(text, {
        latitude,
        longitude,
        radius: radiusKm,
        sessionToken: ensureSessionToken(),
        tagging_mode,
        types,
        accessibilities,
        showVenueOptions,
      });
    },
    [
      latitude,
      longitude,
      radiusKm,
      tagging_mode,
      types,
      accessibilities,
      showVenueOptions,
      ensureSessionToken,
      isSyntheticLabel,
    ],
  );

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onValueChange(text);

    if (!enableAutoSuggest || suppressDropdown) return;

    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
    }

    if (text && text.length >= 1) {
      ensureSessionToken();
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);

      if (text.length >= 2) {
        suggestDebounceRef.current = window.setTimeout(async () => {
          const list = await fetchSuggestions(text);
          setItems(list);
          setIsSuggestOpen(true);
        }, 200);
      }
    } else {
      // Input cleared — end the session, show the empty-state dropdown.
      resetSessionToken();
      clearSuggestions();
      if (showSearchSuggestions) {
        setIsSearchSuggestionsOpen(true);
      }
    }
  };

  const handleClear = () => {
    trackEvent("search_cleared", {});
    onValueChange("");
    resetSessionToken();
    clearSuggestions();
    if (onClear) onClear();
  };

  const handleEnter = () => {
    if (isSuggestOpen) return;
    clearSuggestions();
    onSubmit(value);
  };

  const handleFocus = useCallback(async () => {
    if (suppressDropdown) return;

    const rawText = value.trim();
    // "Nearby" / "Current location" are synthetic context labels, not real
    // input — clear them on focus so the box starts empty (ghost text +
    // default dropdown) instead of making users delete it first.
    const isSynthetic = isSyntheticLabel(rawText);
    if (isSynthetic) onValueChange("");
    const text = isSynthetic ? "" : rawText;

    if (text.length === 0 && showSearchSuggestions) {
      // Overlay layouts fire viewed on mount instead (see the effect above).
      if (!isOverlayLayout) trackEvent("search_list_viewed", {});
      setIsSearchSuggestionsOpen(true);
      setIsSuggestOpen(false);
      return;
    }

    if (!enableAutoSuggest) return;

    if (text.length >= 2) {
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);
      const list = await fetchSuggestions(text);
      setItems(list);
    } else if (text.length === 1) {
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);
      setItems([]);
    }
  }, [
    value,
    showSearchSuggestions,
    suppressDropdown,
    fetchSuggestions,
    isOverlayLayout,
    isSyntheticLabel,
    onValueChange,
  ]);

  const commitSelection = (
    item:
      | { kind: "query"; text: string }
      | { kind: "item"; data: UnifiedSuggestItem },
  ) => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setIsSuggestOpen(false);
    setHighlightIndex(-1);
    setItems([]);

    if (item.kind === "query") {
      // Free-text search — no Google call, so the session token is irrelevant.
      resetSessionToken();
      onSubmit(sanitizeInput(value));
      return;
    }

    // Hand the action + live session token to the consumer, then reset.
    const token = sessionTokenRef.current;
    onSelectUnified(item.data, token);
    resetSessionToken();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSearchSuggestionsOpen && value.length === 0) {
      return;
    }

    if (!enableAutoSuggest) return;
    if (!isSuggestOpen || displayItems.length === 0) {
      if (e.key === "ArrowDown" && items.length > 0) {
        setIsSuggestOpen(true);
        setHighlightIndex((prev) => (prev < 0 ? 0 : (prev + 1) % items.length));
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      setHighlightIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % displayItems.length,
      );
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightIndex((prev) =>
        prev <= 0 ? displayItems.length - 1 : prev - 1,
      );
      e.preventDefault();
    } else if (e.key === "Enter") {
      const indexToUse = highlightIndex >= 0 ? highlightIndex : 0;
      const item = displayItems[indexToUse];
      if (item) {
        commitSelection(item);
      }
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === "Escape") {
      clearSuggestions();
      e.preventDefault();
    }
  };

  const handleClickItem = (
    item:
      | { kind: "query"; text: string }
      | { kind: "item"; data: UnifiedSuggestItem },
  ) => {
    commitSelection(item);
  };

  // Handlers for SearchSuggestionsDropdown
  const handleNearbyPlacesClick = () => {
    clearSuggestions();
    onNearbyPlacesClick?.();
  };

  const handleRecentSearchSelect = (search: RecentSearchResult) => {
    clearSuggestions();
    onRecentSearchSelect?.(search);
  };

  const handleNeighborhoodSelect = (neighborhood: NeighborhoodSuggestion) => {
    clearSuggestions();
    onNeighborhoodSelect?.(neighborhood);
  };

  const handleLoginPress = () => {
    clearSuggestions();
    onLoginPress?.();
  };

  const handleSearchSuggestionsClose = () => {
    setIsSearchSuggestionsOpen(false);
  };

  const handleContainerBlur = () => {
    setTimeout(() => {
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        clearSuggestions();
      }
    }, 0);
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      onBlur={handleContainerBlur}
    >
      <FloatingLabelInput
        ref={inputRef}
        label={label}
        type="text"
        value={value}
        onChange={handleChange}
        onClear={handleClear}
        onEnter={handleEnter}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        borderless={true}
        role="combobox"
        aria-expanded={
          (isSuggestOpen && value.length > 0) ||
          (isSearchSuggestionsOpen && value.length === 0)
        }
        aria-controls={
          isSuggestOpen && value.length > 0
            ? "suggestions-listbox"
            : isSearchSuggestionsOpen && value.length === 0
              ? "search-suggestions-listbox"
              : undefined
        }
        aria-activedescendant={
          highlightIndex >= 0 ? `suggest-item-${highlightIndex}` : undefined
        }
        aria-autocomplete="list"
      />

      {/* SearchSuggestionsDropdown - shown when input is empty and focused */}
      {showSearchSuggestions &&
        isSearchSuggestionsOpen &&
        value.length === 0 && (
          <div className="absolute z-20 left-[-30px] md:left-0 right-0 top-full mt-2">
            <SearchSuggestionsDropdown
              isOpen={true}
              onNearbyPlacesClick={handleNearbyPlacesClick}
              onRecentSearchSelect={handleRecentSearchSelect}
              onNeighborhoodSelect={handleNeighborhoodSelect}
              onLoginPress={handleLoginPress}
              onClose={handleSearchSuggestionsClose}
              onKeyboardNavigate={() => {}}
              latitude={latitude}
              longitude={longitude}
            />
          </div>
        )}

      {/* Autosuggest dropdown - shown when user is typing */}
      {enableAutoSuggest && isSuggestOpen && value.length > 0 && (
        <div
          ref={suggestListRef}
          id="suggestions-listbox"
          role="listbox"
          className="absolute z-50 left-0 right-0 top-full mt-2 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded shadow-lg max-h-[32rem] overflow-y-auto"
        >
          {displayItems.map((item, idx) => {
            return (
              <Fragment key={`item-${idx}`}>
                <button
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={idx === highlightIndex}
                  className={cn(
                    "w-full text-left px-4 py-2.5 focus:outline-none transition-colors",
                    idx === highlightIndex
                      ? "bg-bg-brand-tertiary-active "
                      : "hover:bg-container-neutral-minimal",
                  )}
                  id={`suggest-item-${idx}`}
                  onClick={() => handleClickItem(item)}
                >
                  {item.kind === "query" ? (
                    <div className="flex items-center gap-2 w-full">
                      <IconBadge tone="grey">
                        <MagnifyingGlassIcon
                          className="text-fg-neutral-medium"
                          size={18}
                        />
                      </IconBadge>
                      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                          {value}
                        </span>
                        <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                          {i18n.get("pressEnterToSearch")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex items-center justify-center flex-shrink-0">
                        {renderItemIcon(item.data)}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                          {renderHighlighted(
                            item.data.displayText,
                            item.data.highlightRanges,
                          )}
                        </span>
                        {subtitleLine(item.data) && (
                          <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                            {subtitleLine(item.data)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
